import React, { useEffect } from "react";
import {
  DeviceCalibrationScreenVMProvider,
  useDeviceCalibrationScreenVM,
} from "@screens/DeviceCalibrationScreen/DeviceCalibrationScreenVM";
import { observer } from "mobx-react-lite";
import { Navigate, useSearchParams } from "react-router-dom";
import { ROUTES } from "@stores/AccountStore";
import DeviceCalibrationMenu from "@screens/DeviceCalibrationScreen/DeviceCalibrationMenu";
import styled from "@emotion/styled";
import LeftBar from "@components/Layout/LeftBar";
import DeviceCalibrationTable from "@screens/DeviceCalibrationScreen/DeviceCalibrationTable";
import DeviceCalibrationDialog from "@screens/DeviceCalibrationScreen/dialogs/DeviceCalibrationDialog";
import useIsMobile, { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";
import MobileTabBar, { TAB_BAR_HEIGHT } from "@components/MobileTabBar";

const Root = styled.div`
  display: flex;
  min-height: 100vh;
  width: 100%;
  align-items: stretch;

  @media (min-width: ${MOBILE_BREAKPOINT + 1}px) {
    min-width: 1152px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    /* Clear the fixed tab bar, same allowance Layout uses, so the last
       calibration card scrolls fully above it. */
    padding-bottom: calc(
      ${TAB_BAR_HEIGHT + 24}px + var(--tabbar-inset, env(safe-area-inset-bottom, 0px))
    );
  }
`;

const DeviceCalibrationScreenImpl = observer(() => {
  const vm = useDeviceCalibrationScreenVM();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const start = searchParams.get("start");
    const stop = searchParams.get("stop");
    if (start && stop && vm.device) {
      vm.setPrefillFromTracks({ start, stop });
      vm.setDialogVisible(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, vm.device, vm, setSearchParams]);

  if (vm.device == null && vm.initialized) {
    return <Navigate to={ROUTES.DEVICES} />;
  }

  return (
    <Root>
      <LeftBar style={{ marginRight: 0 }} />
      <DeviceCalibrationMenu />
      <DeviceCalibrationTable />
      <DeviceCalibrationDialog
        destroyOnClose
        id={vm.editCalibrationId}
        onSubmit={
          vm.editCalibrationId == null
            ? vm.createCalibration
            : vm.editCalibration
        }
        visible={vm.dialogVisible}
        onClose={() => vm.setDialogVisible(false)}
      />
      {/* LeftBar renders nothing on a phone, so without this the only way off
          this screen is the "Devices" back link in the header. */}
      {isMobile && <MobileTabBar />}
    </Root>
  );
});

const DeviceCalibrationScreen = () => {
  return (
    <DeviceCalibrationScreenVMProvider>
      <DeviceCalibrationScreenImpl />
    </DeviceCalibrationScreenVMProvider>
  );
};

export default DeviceCalibrationScreen;
